import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Breadcrumb, Button } from "tvs-unified-lib";
import {
  CAMPAIGN_STATUS,
  CHANNEL,
  optionsAccountType,
  optionsAgeBand,
  optionsBucket,
  optionsChannelPropensity,
  optionsCollectorGender,
  optionsCollectorSegment,
  optionsCurrentMonthBounce,
  optionsCustomerRiskProfile,
  optionsDataSource,
  optionsFirstEmi,
  optionsItinerary,
  optionsMob,
  optionsOccupation,
  optionsOnlinePaidEver,
  optionsOverdueAmountBand,
  optionsPaidLast3Month,
  optionsPaymentFrequency,
  optionsPerformanceRating,
  optionsPropensityToPay,
  optionsRepeat,
  optionsRepeatEvery,
  optionsSecondEmi,
  optionsSpecificDay,
  optionsThirdEmi,
  optionsTimeBand,
  optionsTriggerBy,
  optionsValueAtRisk,
  optionsAction,
  SCHEDULE_MODE,
  YES_NO,
  optionsWhen as optionsWhenForReferOut,
  NUMERIC_COMPARISON_OPTIONS,
  PRIORITY_LEVEL_OPTIONS,
  BOOLEAN_CHOICE_OPTIONS,
  EQUALITY_COMPARISON_OPTIONS,
} from "../constant";

import ConfirmationDialog from "@/components/ConfirmationDialog";
import { SelectOptionType } from "@/components/SelectField";
import { APP_ROUTES } from "@/routers/routes";
import {
  useCampaignCreate,
  useCampaignDetail,
  useCampaignUpdate,
} from "@/services/hooks/campaign/useCampaign";
import useCampaignStore from "@/store/campaignStore";
import {
  createPayload,
  editPayload,
  schema,
  transformApiReferOutToForm,
} from "../helpers";
import { CampaignForm, ConditionItem } from "../types";
import { LeftLayoutForm } from "./components/left-layout-form";
import { RightLayoutForm } from "./components/right-layout-form";
import { Body, ButtonContainer, Root, Title } from "./styles";

const formCampaignDefaultValues = {
  repeat_end: "repeat_end_never",
  repeat: [optionsRepeat[0]],
  campaign_channels: [
    {
      channel: CHANNEL.SMS,
      specific_day_check: undefined,
      number_of_day_check: undefined,
      is_check_day: SCHEDULE_MODE.SPECIFIC_DATE,
      [`campaign_data_channels`]: [
        {
          day: undefined,
          time_band: undefined,
          template_script_id: undefined,
        },
      ],
      [`refer_out`]: [
        {
          type: "",
          when: [],
          condition: [],
        },
      ],
    },
  ],
};

export const FormCampaign = () => {
  const navigator = useNavigate();

  const { id } = useParams();
  const [isEdit, setIsEdit] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors },
    trigger,
  } = useForm<CampaignForm>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(schema) as any,
    mode: "all",
    defaultValues: formCampaignDefaultValues,
  });

  const { initializeCampaignDetail } = useCampaignStore();

  const {
    data: campaignDetail,
    refetch: refetchCampaignDetail,
    isError: isErrorCampaignDetail,
  } = useCampaignDetail(id || "");
  const { mutation: createCampaign, isPending: isCreateCampaignPending } =
    useCampaignCreate();
  const { mutation: updateCampaign, isPending: isUpdateCampaignPending } =
    useCampaignUpdate();

  useEffect(() => {
    if (!id) {
      reset({
        ...formCampaignDefaultValues,
        status: CAMPAIGN_STATUS.DRAFT,
        repeat_end: "repeat_end_never",
        repeat_end_never: true,
        repeat_frequency: [optionsRepeatEvery[0]],
      });
      setIsEdit(false);
    } else {
      setIsEdit(true);
      refetchCampaignDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (isErrorCampaignDetail) {
      navigator(APP_ROUTES.NOT_FOUND.to);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isErrorCampaignDetail]);

  useEffect(() => {
    const derivedStateOptionsForHelper = {
      NUMERIC_COMPARISON_OPTIONS,
      PRIORITY_LEVEL_OPTIONS,
      BOOLEAN_CHOICE_OPTIONS,
      EQUALITY_COMPARISON_OPTIONS,
    };
    if (campaignDetail) {
      const { data } = campaignDetail;
      reset({
        status: data?.status,
        name: data?.name,
        campaign_id: data?.campaignId,
        start_date: data?.startDate,
        end_date: data?.endDate,
        createdAt: data?.createdAt,
        updatedAt: data?.updatedAt,
        createdBy:
          data?.createdUser?.firstName + " " + data?.createdUser?.lastName,
        data_source: [
          optionsDataSource.find(
            (item: SelectOptionType) => item.value === data?.dataSource
          ),
        ],
        repeat: [
          optionsRepeat.find(
            (item: SelectOptionType) => item.value === data?.repeat.toString()
          ),
        ],

        ...(data.repeat.toString() === "true" && {
          repeat_frequency: [
            optionsRepeatEvery.find(
              (option: SelectOptionType) =>
                option.value.toLowerCase() ===
                data?.repeatFrequency.toLowerCase()
            ),
          ],
          repeat_end: data?.repeatEnd,
          ...(data?.repeatEnd === "repeat_end_never" && {
            repeat_end_never: true,
          }),
          ...(data?.repeatEnd === "repeat_end_after_number_of_occurrences" && {
            repeat_end_after_number_of_occurrences:
              data?.repeatEndAfterNumberOfOccurrences,
          }),
          ...(data?.repeatEnd === "repeat_end_on" && {
            repeat_end_on: data?.repeatEndOn,
          }),
        }),

        remarks: data?.remarks,
        campaign_channels: data?.campaignChannels.map((channel: any) => ({
          channel: channel.channel,

          trigger_by:
            typeof channel.triggerBy === "string"
              ? [
                  optionsTriggerBy.find(
                    (option: SelectOptionType) =>
                      option.value === channel.triggerBy
                  ),
                ]
              : undefined,

          action:
            typeof channel.action === "string"
              ? [
                  optionsAction.find(
                    (option: SelectOptionType) =>
                      option.value === channel.action
                  ),
                ]
              : undefined,

          is_check_day: channel.numberOfDayCheck
            ? SCHEDULE_MODE.NUMBER_OF_DAYS
            : SCHEDULE_MODE.SPECIFIC_DATE,

          specific_day_check: channel.specificDayCheck,
          number_of_day_check: channel.numberOfDayCheck,
          main_operator: channel.mainOperator || "",
          previous_month_allocation: channel?.previousMonthAllocation,
          collection_success: channel.collectionSuccess,
          collection_failure: channel.collectionFailure,
          allocation_status: channel?.allocationStatus || [],
          allocation_limit: channel.allocationLimit,
          bucket_wise_load_balancing:
            channel.bucketWiseLoadBalancing === null
              ? null
              : channel.bucketWiseLoadBalancing
              ? YES_NO.YES
              : YES_NO.NO,
          km_range: channel.kmRange,
          acr: channel.acr,
          collector_segment: optionsCollectorSegment?.filter((item) => {
            return channel.collectorSegment?.includes(item.value);
          }),
          collector_gender: optionsCollectorGender.filter((item) => {
            return channel.collectorGender?.includes(item.value);
          }),
          performance_rating: optionsPerformanceRating.filter((item) => {
            return channel.performanceRating?.includes(item.value);
          }),
          itinerary: optionsItinerary.filter((item) => {
            return channel.itinerary?.includes(item.value);
          }),
          location: channel?.state
            ? [
                {
                  data: {
                    state: channel?.state,
                    city: channel?.district,
                    area: channel?.area,
                  },
                  value: `${channel?.area?.id}`,
                  label: `${channel?.state?.name}, ${channel?.district?.name}, ${channel?.area?.name}`,
                },
              ]
            : [],
          portfolio: channel?.portfolioCategory
            ? [channel.portfolioCategory.id]
            : null,
          campaign_data_channels: channel.campaignDataChannels?.map(
            (dataChannel: any) => ({
              day:
                dataChannel?.day
                  ?.map((selectedDayValue: string) => {
                    return optionsSpecificDay?.find(
                      (option: SelectOptionType) =>
                        option.value === selectedDayValue
                    );
                  })
                  .filter((option: SelectOptionType) => option !== undefined) ||
                [],
              number_of_days: dataChannel?.numberOfDays,
              time_band:
                dataChannel?.timeBand
                  ?.map((selectedTimeBandValue: string) => {
                    return optionsTimeBand?.find(
                      (option: SelectOptionType) =>
                        option.value === selectedTimeBandValue
                    );
                  })
                  .filter((option: SelectOptionType) => option !== undefined) ||
                [],
            })
          ),
          additional_parameters: channel.additionalParameters
            ? {
                ops: channel.additionalParameters.ops ?? [],
                nodes: channel.additionalParameters.nodes ?? [],
              }
            : undefined,
          // TODO: need improve
          refer_out: transformApiReferOutToForm(channel.referOut, {
            optionsWhenMasterList: optionsWhenForReferOut,
            derivedStateOptions: derivedStateOptionsForHelper,
          }).map((referOut: ConditionItem) => ({
            type: referOut.type,
            when: optionsWhenForReferOut.filter(
              (option: SelectOptionType) =>
                option.value ===
                (referOut.when as unknown as SelectOptionType)?.value
            ),
            condition: referOut.condition_options?.filter(
              (option: SelectOptionType) =>
                option.value ===
                (referOut.condition as unknown as SelectOptionType)?.value
            ),
            // refill action value
            action: referOut.action,
            action_type_numeric: referOut.action_type_numeric,
            action_type_boolean: referOut.action_type_boolean,
            action_type_string: referOut.action_type_string,
            action_type_date: referOut.action_type_date,
            action_type_dropdown: referOut.action_type_dropdown,

            // refill condition value
            condition_options: referOut.condition_options,
            action_display_type: referOut.action_display_type,
            action_validation_type: referOut.action_validation_type,
            action_options: referOut.action_options,
            action_placeholder: referOut.action_placeholder,
            action_is_disabled: referOut.action_is_disabled,

            // refill operator value
            // if last refer out, then use OR
            current_operator: referOut.current_operator || "OR",
          })),
        })),

        occupation: data?.customerParams?.occupation
          ? // convert string to array
            data?.customerParams?.occupation
              .map((item: string) => {
                return optionsOccupation?.find(
                  (option: SelectOptionType) => option.value === item
                );
              })
              .filter((option: SelectOptionType) => option !== undefined) || []
          : [],

        age_band: data?.customerParams?.ageBand
          ? data?.customerParams?.ageBand
              .map((item: string) => {
                return optionsAgeBand?.find(
                  (option: SelectOptionType) => option.value === item
                );
              })
              .filter((option: SelectOptionType) => option !== undefined) || []
          : [],

        customer_risk_profile: data?.customerParams?.customerRiskProfile
          ? data?.customerParams?.customerRiskProfile
              .map((item: string) => {
                return optionsCustomerRiskProfile?.find(
                  (option: SelectOptionType) => option.value === item
                );
              })
              .filter((option: SelectOptionType) => option !== undefined) || []
          : [],

        channel_propensity: data?.customerParams?.channelPropensity
          ? data?.customerParams?.channelPropensity
              .map((item: string) => {
                return optionsChannelPropensity?.find(
                  (option: SelectOptionType) => option.value === item
                );
              })
              .filter((option: SelectOptionType) => option !== undefined) || []
          : [],

        propensity_to_pay: data?.customerParams?.propensityToPay
          ? data?.customerParams?.propensityToPay
              .map((item: string) => {
                return optionsPropensityToPay?.find(
                  (option: SelectOptionType) => option.value === item
                );
              })
              .filter((option: SelectOptionType) => option !== undefined) || []
          : [],

        paid_last_three_months:
          typeof data?.customerParams?.paidLastThreeMonths === "boolean"
            ? ([
                optionsPaidLast3Month.find((option: SelectOptionType) => {
                  const conditionValue = data.customerParams.paidLastThreeMonths
                    ? YES_NO.YES
                    : YES_NO.NO;
                  return option.value === conditionValue;
                }),
              ].filter(Boolean) as SelectOptionType[])
            : undefined,

        online_paid_ever:
          typeof data?.customerParams?.onlinePaidEver === "boolean"
            ? ([
                optionsOnlinePaidEver.find((option: SelectOptionType) => {
                  const conditionValue = data.customerParams.onlinePaidEver
                    ? YES_NO.YES
                    : YES_NO.NO;
                  return option.value === conditionValue;
                }),
              ].filter(Boolean) as SelectOptionType[])
            : undefined,

        current_month_bounce:
          typeof data?.customerParams?.currentMonthBounce === "boolean"
            ? ([
                optionsCurrentMonthBounce.find((option: SelectOptionType) => {
                  const conditionValue = data.customerParams.currentMonthBounce
                    ? YES_NO.YES
                    : YES_NO.NO;
                  return option.value === conditionValue;
                }),
              ].filter(Boolean) as SelectOptionType[])
            : undefined,

        // Loan Parameters
        account_type: data?.loanParams?.accountType
          ? data?.loanParams?.accountType
              .map((item: string) => {
                return optionsAccountType?.find(
                  (option: SelectOptionType) => option.value === item
                );
              })
              .filter((option: SelectOptionType) => option !== undefined) || []
          : undefined,

        mob:
          typeof data?.loanParams?.mob === "string"
            ? [
                optionsMob.find(
                  (option: SelectOptionType) =>
                    option.value === data?.loanParams?.mob
                ),
              ]
            : undefined,

        bucket: data?.loanParams?.bucket
          ? data?.loanParams?.bucket
              .map((item: string) => {
                return optionsBucket?.find(
                  (option: SelectOptionType) => option.value === item
                );
              })
              .filter((option: SelectOptionType) => option !== undefined) || []
          : undefined,

        overdue_amount_band: data?.loanParams?.overdueAmountBand
          ? data?.loanParams?.overdueAmountBand
              .map((item: string) => {
                return optionsOverdueAmountBand?.find(
                  (option: SelectOptionType) => option.value === item
                );
              })
              .filter((option: SelectOptionType) => option !== undefined) || []
          : undefined,

        value_at_risks: data?.loanParams?.valueAtRisks
          ? data?.loanParams?.valueAtRisks
              .map((item: string) => {
                return optionsValueAtRisk?.find(
                  (option: SelectOptionType) => option.value === item
                );
              })
              .filter((option: SelectOptionType) => option !== undefined) || []
          : undefined,

        first_emi:
          typeof data?.loanParams?.firstEmi === "string"
            ? [
                optionsFirstEmi.find(
                  (option: SelectOptionType) =>
                    option.value === data?.loanParams?.firstEmi
                ),
              ]
            : undefined,

        second_emi:
          typeof data?.loanParams?.secondEmi === "string"
            ? [
                optionsSecondEmi.find(
                  (option: SelectOptionType) =>
                    option.value === data?.loanParams?.secondEmi
                ),
              ]
            : undefined,

        third_emi:
          typeof data?.loanParams?.thirdEmi === "string"
            ? [
                optionsThirdEmi.find(
                  (option: SelectOptionType) =>
                    option.value === data?.loanParams?.thirdEmi
                ),
              ]
            : undefined,

        payment_frequency:
          typeof data?.loanParams?.paymentFrequency === "string"
            ? [
                optionsPaymentFrequency.find(
                  (option: SelectOptionType) =>
                    option.value === data?.loanParams?.paymentFrequency
                ),
              ]
            : undefined,
      });
    }
  }, [campaignDetail, reset]);

  useEffect(() => {
    if (campaignDetail && campaignDetail.data) {
      initializeCampaignDetail(campaignDetail.data);
    }
  }, [campaignDetail, initializeCampaignDetail]);

  // handle submit form
  const onSubmit = async (data: CampaignForm) => {
    if (isCreateCampaignPending || isUpdateCampaignPending) return;

    if (isEdit) {
      const payload = editPayload(data);
      updateCampaign.mutate({
        id: id,
        data: payload,
      });
    } else {
      const payload = createPayload(data);
      createCampaign.mutate(payload);
    }
  };

  const handleConfirm = () => {
    setIsConfirmOpen(false);
    navigator("/campaigns");
  };

  const handleClose = () => {
    setIsConfirmOpen(false);
  };

  return (
    <Root
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onSubmit={handleSubmit(onSubmit, (error) => {
        // TODO: remove if build productions
        // console.log("submit error", error);
      })}
    >
      <Breadcrumb
        items={[
          { content: <a href="/campaigns">Campaigns</a> },
          {
            content: <a href="#">{isEdit ? `Edit` : `Add`} Campaigns</a>,
            isActive: true,
          },
        ]}
      />
      <Title>{isEdit ? `Edit` : `Add New`} Campaigns</Title>
      <Body>
        <LeftLayoutForm
          isEdit={isEdit}
          control={control}
          getValues={getValues}
          setValue={setValue}
          watch={watch}
          errors={errors}
          trigger={trigger}
        />
        <RightLayoutForm
          isEdit={isEdit}
          control={control}
          setValue={setValue}
          watch={watch}
        />
      </Body>
      <ButtonContainer>
        <Button
          label="Cancel"
          variant="outline"
          onClick={() => setIsConfirmOpen(true)}
        />
        <Button
          disabled={isCreateCampaignPending || isUpdateCampaignPending}
          label="Save"
          type="submit"
        />
      </ButtonContainer>
      <ConfirmationDialog
        content="Are you sure you want to cancel?"
        actions={[
          {
            id: "no-action",
            label: "No",
            onClick: handleClose,
            variant: "outline",
          },
          {
            id: "yes-action",
            label: "Yes",
            onClick: handleConfirm,
            variant: "primary",
          },
        ]}
        onClose={handleClose}
        open={isConfirmOpen}
      />
    </Root>
  );
};

export default FormCampaign;
